import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Loader2, Upload, X } from 'lucide-react';
import { notifyError, notifySuccess } from '../../lib/notify';
import { LOCAL_DEAL_CATEGORIES } from '../../constants/localDeals';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

const EMPTY = {
  id: '', category: '', name: '', city: '', description: '',
  discount: '', valid_until: '', url: '', image_url: '',
};

const DealForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadCloudinaryScript(); }, []);

  const openUploadWidget = () => {
    if (!window.cloudinary || !CLOUD_NAME || !UPLOAD_PRESET) {
      notifyError('Cloudinary is not configured.');
      return;
    }
    setUploading(true);
    const w = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'],
        multiple: false,
        resourceType: 'image',
        folder: 'mundalo_local_deals',
        maxFileSize: 5_000_000,
        cropping: false,
      },
      (err, result) => {
        if (err) {
          setUploading(false);
          notifyError('Upload failed.');
          return;
        }
        if (result && result.event === 'success') {
          setForm((f) => ({ ...f, image_url: result.info.secure_url }));
          setUploading(false);
          w.close();
        }
        if (result && result.event === 'close') setUploading(false);
      },
    );
    w.open();
  };

  const handleSave = async () => {
    for (const k of ['id', 'category', 'name', 'city', 'description', 'discount', 'valid_until', 'url']) {
      if (!form[k]?.toString().trim()) {
        notifyError(`Field "${k}" is required.`);
        return;
      }
    }
    setSaving(true);
    const isUpdate = !!initial;
    const url = isUpdate ? `${API}/api/admin/local-deals/${initial.id}` : `${API}/api/admin/local-deals`;
    const method = isUpdate ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        notifySuccess(isUpdate ? 'Deal updated.' : 'Deal created.');
        onSaved();
      } else {
        notifyError(await r.json().catch(() => ({})), 'Save failed.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-local-deal-form">
      <div className="absolute inset-0 bg-[#0A1F2E]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-[#EFE6CF] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#0A1F2E]/40 hover:text-[#0A1F2E]" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-5">
          {initial ? 'Edit local deal' : 'Add a local deal'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Slug (id)" value={form.id} onChange={(v) => setForm({ ...form, id: v })} disabled={!!initial} placeholder="e.g. cowork-merida-month" />
          <SelectField
            label="Category"
            value={form.category}
            onChange={(v) => setForm({ ...form, category: v })}
            options={LOCAL_DEAL_CATEGORIES}
            placeholder="Pick a category…"
          />
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="WeWork CDMX - Day Pass" />
          <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} placeholder="Mexico City" />
          <Field label="Discount" value={form.discount} onChange={(v) => setForm({ ...form, discount: v })} placeholder="20% off first 5 day passes" />
          <Field label="Valid until" value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} placeholder="2026-12-31" type="date" />
          <div className="md:col-span-2">
            <Field label="Redemption URL" value={form.url} onChange={(v) => setForm({ ...form, url: v })} placeholder="https://…" />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            />
          </div>

          {/* Image upload */}
          <div className="md:col-span-2">
            <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">
              Logo / image
            </label>
            <div className="flex items-center gap-4">
              {form.image_url ? (
                <img
                  src={form.image_url}
                  alt="logo preview"
                  className="w-24 h-24 object-cover rounded-lg border border-[#EFE6CF]"
                  data-testid="deal-image-preview"
                />
              ) : (
                <div className="w-24 h-24 bg-[#F4ECD8] rounded-lg border border-[#EFE6CF] flex items-center justify-center text-[#0A1F2E]/35 text-xs">
                  No image
                </div>
              )}
              <button
                type="button"
                onClick={openUploadWidget}
                disabled={uploading}
                className="inline-flex items-center gap-2 bg-[#2CA8A0] hover:bg-[#1A8E86] text-white font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full transition disabled:opacity-60"
                data-testid="deal-image-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {form.image_url ? 'Replace image' : 'Upload image'}
              </button>
              {form.image_url && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, image_url: '' })}
                  className="text-[#B91C1C] hover:text-[#7F1414] text-sm"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition disabled:opacity-50"
            data-testid="deal-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (initial ? 'Save changes' : 'Create deal')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-white border border-[#EFE6CF] text-[#0A1F2E]/70 font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full hover:border-[#0A1F2E]/40 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, disabled, type = 'text' }) => (
  <div>
    <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40 disabled:bg-[#F4ECD8]/50 disabled:cursor-not-allowed"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div>
    <label className="block font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
      data-testid={`deal-form-${label.toLowerCase()}`}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

const AdminLocalDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | 'new' | dealObj
  const [cities, setCities] = useState([]);
  const [cityFilter, setCityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/local-deals`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setDeals(d.deals || []);
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not load deals.');
      }
    } catch {
      notifyError('Network error loading deals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Canonical city list. Falls back to derived cities if /api/cities is unreachable.
  useEffect(() => {
    fetch(`${API}/api/cities`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const items = Array.isArray(d) ? d : d.cities || [];
        setCities(items.map((c) => c.name).filter(Boolean).sort());
      })
      .catch(() => { /* derive client-side */ });
  }, []);

  const cityOptions = useMemo(() => {
    if (cities.length > 0) return cities;
    return Array.from(new Set(deals.map((d) => d.city).filter(Boolean))).sort();
  }, [cities, deals]);

  // AND filter - both selections must match if set.
  const filteredDeals = useMemo(() => {
    return deals.filter((d) => {
      if (cityFilter && d.city !== cityFilter) return false;
      if (categoryFilter && (d.category || 'Other') !== categoryFilter) return false;
      return true;
    });
  }, [deals, cityFilter, categoryFilter]);

  const deleteDeal = async (id) => {
    if (!window.confirm(`Delete deal "${id}"? This can't be undone.`)) return;
    try {
      const r = await fetch(`${API}/api/admin/local-deals/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) {
        notifySuccess('Deal deleted.');
        reload();
      } else {
        notifyError(await r.json().catch(() => ({})), 'Delete failed.');
      }
    } catch {
      notifyError('Network error.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]" data-testid="admin-local-deals-page">
      <header className="border-b border-[#EFE6CF] bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center gap-4">
          <Link to="/admin/dashboard" className="text-[#0A1F2E]/55 hover:text-[#0A1F2E] transition">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-display italic text-2xl text-[#0A1F2E]">Local Deals</h1>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="ml-auto inline-flex items-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full transition"
            data-testid="admin-new-deal-btn"
          >
            <Plus className="w-4 h-4" /> Add deal
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Filters - city + category. AND logic, instant client-side. */}
        <div
          className="flex flex-wrap items-center gap-3 mb-6"
          data-testid="admin-local-deals-filters"
        >
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            data-testid="admin-local-deals-filter-city"
          >
            <option value="">All Cities</option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            data-testid="admin-local-deals-filter-category"
          >
            <option value="">All Categories</option>
            {LOCAL_DEAL_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {(cityFilter || categoryFilter) && (
            <button
              type="button"
              onClick={() => { setCityFilter(''); setCategoryFilter(''); }}
              className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 hover:text-[#0A1F2E] px-3 py-2 transition"
              data-testid="admin-local-deals-filter-clear"
            >
              Clear filters
            </button>
          )}
          <span
            className="ml-auto font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55"
            data-testid="admin-local-deals-result-count"
          >
            {filteredDeals.length} of {deals.length}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#0A1F2E]/55">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : deals.length === 0 ? (
          <p className="text-center py-20 text-[#0A1F2E]/55">No local deals yet - click "Add deal" to create one.</p>
        ) : filteredDeals.length === 0 ? (
          <p className="text-center py-20 text-[#0A1F2E]/55" data-testid="admin-local-deals-empty">
            No deals match these filters.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="admin-local-deals-table">
            <thead>
              <tr className="text-left font-mono uppercase text-[10px] tracking-[0.12em] text-[#0A1F2E]/55 border-b border-[#EFE6CF]">
                <th className="py-3 pr-3">Logo</th>
                <th className="py-3 pr-3">Name</th>
                <th className="py-3 pr-3">Category</th>
                <th className="py-3 pr-3">City</th>
                <th className="py-3 pr-3">Valid until</th>
                <th className="py-3 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((d) => (
                <tr key={d.id} className="border-b border-[#EFE6CF]/60" data-testid={`admin-deal-row-${d.id}`}>
                  <td className="py-3 pr-3">
                    {d.image_url ? (
                      <img src={d.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-[#F4ECD8] rounded" />
                    )}
                  </td>
                  <td className="py-3 pr-3 font-medium text-[#0A1F2E]">{d.name}</td>
                  <td className="py-3 pr-3 text-[#0A1F2E]/70">{d.category}</td>
                  <td className="py-3 pr-3 text-[#0A1F2E]/70">{d.city}</td>
                  <td className="py-3 pr-3 text-[#0A1F2E]/70">{d.valid_until}</td>
                  <td className="py-3 pr-3 flex gap-2 justify-end">
                    <button onClick={() => setEditing(d)} className="text-[#1A5C6E] hover:text-[#0A1F2E]" data-testid={`admin-deal-edit-${d.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteDeal(d.id)} className="text-[#B91C1C] hover:text-[#7F1414]" data-testid={`admin-deal-delete-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>

      {editing && (
        <DealForm
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
};

export default AdminLocalDeals;
